import React from 'react';
import { ConfigProvider, Dropdown } from 'antd';
import type { MenuProps, ThemeConfig } from 'antd';
import { ChevronDown, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NS as NAVIGATION_NS } from '../../../navigation/i18n';
import { SUPPORTED_LANGUAGES, isLanguageId, saveLanguageChoice } from '../../model/languages';
import './LanguageSwitcher.css';

/**
 * The selected language is painted with the secondary colour (accent ramp)
 * instead of the global primary: AntD marks the selected row with
 * `colorPrimary` text on `controlItemBgActive`, which in this theme is lime on
 * a pale yellow — unreadable here. Component-scoped, so every other dropdown
 * keeps the theme's default.
 *
 * `var()` strings are safe for these three: AntD's Dropdown writes them into
 * the CSS as-is (no colour math on them), and they keep following a runtime
 * brand switch, which a hex import would not.
 */
const SELECTED_SECONDARY_THEME: ThemeConfig = {
  components: {
    Dropdown: {
      colorPrimary: 'var(--silia-accent-fg)',
      controlItemBgActive: 'var(--silia-accent-bg-subtle)',
      controlItemBgActiveHover: 'var(--silia-accent-bg)',
    },
  },
};

interface LanguageSwitcherProps {
  /**
   * `floating` — the desktop pill, fixed top-right (prototype design).
   * `menu` — a full-width row for the mobile menu, where the top-right corner
   * belongs to MobileHeader; opens upward since it sits in the menu footer.
   */
  variant?: 'floating' | 'menu';
}

/**
 * Language picker: a floating pill on desktop, a menu row on mobile.
 *
 * Ported from the prototype's `VersionSwitcher` (Design repo), keeping only
 * its "Language" group — the template/page-version groups are prototype
 * tooling, not product.
 *
 * Until the user picks here, the language is whatever the browser/OS reports
 * (re-detected on every load). Picking one stores it as an explicit choice,
 * which the detector reads before the browser language from then on — see
 * LANGUAGE_DETECTION.
 */
export function LanguageSwitcher({
  variant = 'floating',
}: LanguageSwitcherProps): React.ReactElement {
  const { t, i18n } = useTranslation(NAVIGATION_NS);
  const isMenu = variant === 'menu';

  const resolved = i18n.resolvedLanguage;
  const current = isLanguageId(resolved) ? resolved : 'en';
  const currentLabel = SUPPORTED_LANGUAGES.find((lang) => lang.id === current)?.label;

  const items: MenuProps['items'] = [
    {
      key: 'language-header',
      type: 'group',
      label: t('language.groupLabel'),
      children: SUPPORTED_LANGUAGES.map((lang) => ({
        key: lang.id,
        // `lang` on the label so a screen reader pronounces each endonym in its
        // own language instead of the page's.
        label: (
          <span className="language-switcher__option" lang={lang.id}>
            <Languages size={12} className="language-switcher__option-icon" aria-hidden />
            {lang.label}
          </span>
        ),
      })),
    },
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === current || !isLanguageId(key)) return;
    saveLanguageChoice(key);
    void i18n.changeLanguage(key);
  };

  return (
    <div className={`language-switcher language-switcher--${variant}`}>
      <ConfigProvider theme={SELECTED_SECONDARY_THEME}>
        <Dropdown
          menu={{ items, onClick, selectedKeys: [current] }}
          trigger={['click']}
          placement={isMenu ? 'topLeft' : 'bottomRight'}
          overlayStyle={{ minWidth: 180 }}
        >
          <button
            type="button"
            className="language-switcher__trigger"
            aria-label={t('language.ariaLabel')}
            aria-haspopup="menu"
          >
            <Languages size={isMenu ? 16 : 14} className="language-switcher__icon" aria-hidden />
            {isMenu && <span className="language-switcher__title">{t('language.groupLabel')}</span>}
            <span className="language-switcher__label">{currentLabel}</span>
            <ChevronDown size={14} className="language-switcher__chevron" aria-hidden />
          </button>
        </Dropdown>
      </ConfigProvider>
    </div>
  );
}
